import type { CheckoutStep } from '../types'

const ACTIVE_CHECKOUT_SESSION_STORAGE_KEY = 'emporion_active_checkout_session_id'

const CHECKOUT_STEP_TO_QUERY: Record<CheckoutStep, string | null> = {
  cart_review: null,
  shipping_address: 'shipping',
  billing_address: 'billing',
  shipping_method: 'shipping-method',
  review: 'review',
  redirecting_to_payment: 'payment',
  completed: 'payment',
}

const QUERY_TO_CHECKOUT_STEP: Record<string, CheckoutStep> = {
  shipping: 'shipping_address',
  billing: 'billing_address',
  'shipping-method': 'shipping_method',
  review: 'review',
  payment: 'redirecting_to_payment',
}

const buildUrlWithStep = (href: string, step: CheckoutStep): string => {
  const nextUrl = new URL(href)
  const queryValue = CHECKOUT_STEP_TO_QUERY[step]

  nextUrl.searchParams.delete('sessionId')
  nextUrl.searchParams.delete('session')

  if (queryValue) {
    nextUrl.searchParams.set('step', queryValue)
  } else {
    nextUrl.searchParams.delete('step')
  }

  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
}

export const parseCheckoutStepFromSearch = (search: string): CheckoutStep | null => {
  const value = new URLSearchParams(search).get('step')?.trim().toLowerCase()
  if (!value) return null
  return QUERY_TO_CHECKOUT_STEP[value] ?? null
}

export const replaceCheckoutStepInUrl = (step: CheckoutStep) => {
  if (typeof window === 'undefined') return
  const nextUrl = buildUrlWithStep(window.location.href, step)
  window.history.replaceState(window.history.state, '', nextUrl)
}

export const pushCheckoutStepInUrl = (step: CheckoutStep) => {
  if (typeof window === 'undefined') return
  const nextUrl = buildUrlWithStep(window.location.href, step)
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` === nextUrl) return
  window.history.pushState(window.history.state, '', nextUrl)
}

export const clearLegacyCheckoutSessionQueryFromUrl = () => {
  if (typeof window === 'undefined') return
  const nextUrl = new URL(window.location.href)
  nextUrl.searchParams.delete('sessionId')
  nextUrl.searchParams.delete('session')
  const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` === nextPath) return
  window.history.replaceState(window.history.state, '', nextPath)
}

export const readActiveCheckoutSessionId = (): string | null => {
  if (typeof window === 'undefined') return null
  const value = window.sessionStorage.getItem(ACTIVE_CHECKOUT_SESSION_STORAGE_KEY)?.trim()
  if (!value) return null
  return /^[a-f0-9]{24}$/i.test(value) ? value : null
}

export const writeActiveCheckoutSessionId = (sessionId: string) => {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(ACTIVE_CHECKOUT_SESSION_STORAGE_KEY, sessionId)
}

export const clearActiveCheckoutSessionId = () => {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(ACTIVE_CHECKOUT_SESSION_STORAGE_KEY)
}
